/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { BsSearch } from "react-icons/bs";

import { Input } from "../../ui";
import { BreadCrumbComponent } from "../../ui/BreadCrumbComponent";
import { useDashboardContext } from "../../context/DashboardContext";
import { useAdminContext } from "../../context/admin/AdminContext";
import { addOneLevel } from "../../lib/breadCrumb/BreadCrumb";
import { pushLevel } from "../../lib";
import { getType } from "../contentStats/helper";

import type { Role } from "../../layout/sidebar/types";
import {
  CATEGORY_META,
  CATEGORY_ORDER,
  GEOGRAPHY_NAMES,
  MODULES,
} from "./modules";
import type { ModuleCategory, ModuleItem, StatsRecord } from "./types";
import CategoryTabs from "./CategoryTabs";
import DashboardHero from "./DashboardHero";
import DashboardSection from "./DashboardSection";
import TableStats from "./TableStats";

type Filter = ModuleCategory | "all";

type Props = {
  /** When set, hero renders in "scoped" mode (no greeting). */
  title?: string;
  /** Sub-text under the greeting / title. */
  subtitle?: string;
  /** Eyebrow pill text. */
  eyebrow?: string;
  /** Override stats source (per-entity pages use their own context). */
  stats?: StatsRecord | null;
  /** Fallback route pushed into the breadcrumb trail on card click. */
  clickRoute?: string;
  /** Show the top BreadCrumb (default true). */
  showBreadCrumb?: boolean;
  /** Reset the breadcrumb to its home state (default true — main Dashboard). */
  breadCrumbDefault?: boolean;
  /** Render the regional breakdown table (default true — hide on per-entity pages). */
  showTableStats?: boolean;
  /** Hide the "Geography & Levels" category + tab (set true on per-entity pages). */
  hideGeography?: boolean;
};

const matchesQuery = (m: ModuleItem, q: string, stats?: any) => {
  if (!q) return true;
  const name = m.name.toLowerCase();
  const value = String(stats?.[m.statKey] ?? "").toLowerCase();
  return name.includes(q) || value.includes(q);
};

const DashboardShell: React.FC<Props> = ({
  title,
  subtitle,
  eyebrow,
  stats: statsOverride,
  clickRoute = "/",
  showBreadCrumb = true,
  breadCrumbDefault = true,
  showTableStats = true,
  hideGeography = false,
}) => {
  const { sysAdmin, setSingleAdmin } = useAdminContext();
  const { stats: ctxStats, fetchData } = useDashboardContext();

  const role = sysAdmin?.role as Role | undefined;
  const firstName: string | undefined = sysAdmin?.first_name;

  // If the caller passed its own stats, use that; else fall back to global.
  const stats = statsOverride !== undefined ? statsOverride : ctxStats;

  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<Filter>("all");

  useEffect(() => {
    // Only auto-fetch when we're using the global stats.
    if (statsOverride === undefined && !ctxStats) fetchData();
  }, [ctxStats, statsOverride]);

  const roleVisible = useMemo(
    () =>
      MODULES.filter((m) => {
        if (hideGeography && m.category === "geography") return false;
        return !m.roles || (role ? m.roles.includes(role) : false);
      }),
    [role, hideGeography],
  );

  const visibleModules = useMemo(() => {
    const q = query.trim().toLowerCase();
    return roleVisible.filter((m) => {
      const categoryOk = filter === "all" || m.category === filter;
      return categoryOk && matchesQuery(m, q, stats);
    });
  }, [roleVisible, filter, query, stats]);

  const categoryCounts = useMemo(
    () =>
      CATEGORY_ORDER.map((category) => ({
        category,
        label: CATEGORY_META[category].title,
        count: roleVisible.filter((m) => m.category === category).length,
      })),
    [roleVisible],
  );

  const groupedByCategory = useMemo(() => {
    const buckets = new Map<ModuleCategory, ModuleItem[]>();
    for (const cat of CATEGORY_ORDER) buckets.set(cat, []);
    for (const m of visibleModules) buckets.get(m.category)?.push(m);
    return CATEGORY_ORDER.map((cat) => ({
      category: cat,
      meta: CATEGORY_META[cat],
      modules: buckets.get(cat) ?? [],
    })).filter((b) => b.modules.length > 0);
  }, [visibleModules]);

  const handleCardClick = (module: ModuleItem) => {
    if (GEOGRAPHY_NAMES.has(module.name)) return;
    setSingleAdmin({
      ids: [sysAdmin?.admin_id],
      type: getType({ field: module.typeKey }),
      route: clickRoute,
    });
    addOneLevel({
      id: 0,
      name: "",
      route: "/levels/first",
      level: "",
      country_id: 0,
      data: {
        data: {
          name: "",
          country_id: 0,
          first_level_id: 0,
          second_level_id: 0,
          third_level_id: 0,
          fourth_level_id: 0,
          level: "",
        },
      },
    });
    pushLevel({
      level: {
        id: 0,
        name: "",
        level: "All",
        route: "/levels/first",
        data: null,
      },
    });
  };

  const isLoading = !stats;

  return (
    <div className="relative h-full w-full overflow-y-auto overflow-x-hidden bg-gray-50">
      <div className="mx-auto w-full max-w-7xl space-y-8 px-4 py-6 sm:px-6">
        {showBreadCrumb ? (
          <BreadCrumbComponent default_value={breadCrumbDefault} />
        ) : null}

        <DashboardHero
          title={title}
          subtitle={subtitle}
          eyebrow={eyebrow}
          firstName={firstName}
          stats={stats}
          hideLocations={hideGeography}
        />

        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0 flex-1 overflow-x-auto">
            <CategoryTabs
              selected={filter}
              onChange={setFilter}
              total={roleVisible.length}
              categories={categoryCounts}
            />
          </div>

          <div className="w-full lg:max-w-sm">
            <Input
              type="text"
              value={query}
              placeholder="Search modules…"
              onChange={(e) => setQuery(e.target.value)}
              leadingIcon={<BsSearch className="h-3.5 w-3.5" />}
              className="h-9 text-[13px]"
            />
          </div>
        </div>

        {isLoading ? (
          <LoadingSkeleton />
        ) : groupedByCategory.length === 0 ? (
          <EmptyState query={query} />
        ) : (
          <div className="space-y-10">
            {groupedByCategory.map(({ category, meta, modules }) => (
              <DashboardSection
                key={category}
                meta={meta}
                modules={modules}
                stats={stats}
                onCardClick={handleCardClick}
              />
            ))}
          </div>
        )}

        {showTableStats ? <TableStats /> : null}
      </div>
    </div>
  );
};

const LoadingSkeleton: React.FC = () => (
  <div className="space-y-8">
    {[0, 1].map((section) => (
      <div key={section}>
        <div className="mb-4 flex items-center gap-2.5">
          <span className="h-4 w-[3px] rounded-full bg-brand/30" />
          <div className="h-4 w-40 animate-pulse rounded bg-gray-200" />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className="min-h-[140px] animate-pulse rounded-2xl border border-gray-200 bg-white"
            />
          ))}
        </div>
      </div>
    ))}
  </div>
);

const EmptyState: React.FC<{ query: string }> = ({ query }) => (
  <div className="rounded-2xl border border-dashed border-gray-300 bg-white px-4 py-14 text-center">
    <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-brand/10 text-brand">
      <BsSearch className="h-4 w-4" />
    </div>
    <p className="mt-4 text-[13px] font-medium text-gray-900">
      No modules match {query ? `“${query}”` : "this filter"}.
    </p>
    <p className="mt-1 text-[12px] text-gray-500">
      Try another keyword or clear the filter.
    </p>
  </div>
);

export default DashboardShell;
